// React Error Boundary with terracotta-themed UI, retry, and error reporting.
// Uses class component because error boundaries require componentDidCatch.

import { Component, type ReactNode } from "react";
import { AlertTriangle, RotateCcw, Bug } from "lucide-react";
import { ADMIN_THEME } from "@/lib/admin-theme";

interface ErrorBoundaryProps {
  readonly children: ReactNode;
  /** Optional fallback component. When omitted the built-in terracotta UI is shown. */
  readonly fallback?: ReactNode;
  /** Called when the user clicks "Signaler l'erreur". */
  readonly onReport?: (error: Error, errorInfo: string) => void;
}

interface ErrorBoundaryState {
  readonly hasError: boolean;
  readonly error: Error | null;
  readonly errorInfo: string;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: "" };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    const errorInfo = info.componentStack ?? "";
    this.setState({ errorInfo });
  }

  private handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: "" });
  };

  private handleReport = () => {
    const { error, errorInfo } = this.state;
    if (error && this.props.onReport) {
      this.props.onReport(error, errorInfo);
    }
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    if (this.props.fallback) {
      return this.props.fallback;
    }

    return (
      <div className="flex min-h-[400px] items-center justify-center px-4">
        <div className="w-full max-w-md space-y-6 text-center">
          {/* Icon */}
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full" style={{ background: `${ADMIN_THEME.sidebar.bg}15` }}>
            <AlertTriangle className="h-8 w-8" style={{ color: ADMIN_THEME.sidebar.bg }} />
          </div>

          {/* Message */}
          <div className="space-y-2">
            <h2 className="text-xl font-semibold" style={{ color: ADMIN_THEME.accent.green }}>
              Une erreur est survenue
            </h2>
            <p className="text-sm text-gray-600">
              Quelque chose s'est mal pass&eacute;. Veuillez r&eacute;essayer ou signaler le probl&egrave;me.
            </p>
            {this.state.error && (
              <p className="mx-auto max-w-sm rounded-md bg-red-50 px-3 py-2 text-xs font-mono text-red-700">
                {this.state.error.message}
              </p>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center justify-center gap-3">
            <button
              type="button"
              onClick={this.handleRetry}
              className="inline-flex items-center gap-2 rounded-lg px-5 py-2.5 text-sm font-medium text-white transition-colors hover:opacity-90"
              style={{ background: ADMIN_THEME.sidebar.bg }}
            >
              <RotateCcw className="h-4 w-4" />
              R&eacute;essayer
            </button>

            {this.props.onReport && (
              <button
                type="button"
                onClick={this.handleReport}
                className="inline-flex items-center gap-2 rounded-lg border px-5 py-2.5 text-sm font-medium transition-colors hover:bg-gray-50"
                style={{ borderColor: ADMIN_THEME.content.border, color: ADMIN_THEME.accent.green }}
              >
                <Bug className="h-4 w-4" />
                Signaler l'erreur
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }
}
